import { Flex, Text } from "@mantine/core";
import { DataTableColumn } from "mantine-datatable";
import { useCallback, useMemo } from "react";

import { RestructureApprovalStatus } from "../../../graphql/generated";
import { useCurrentUserContext } from "../../../providers/CurrentUserProvider.tsx";
import { Restructure } from "../../../types/restructure";
import { toLocalDate } from "../../../utils/date.ts";
import { toHumanReadable } from "../../../utils/string";
import { getUserDisplayName, isBababosTenant } from "../../../utils/user.ts";
import ActionIcon from "../../ActionIcon/ActionIcon";
import { DatabaseMetadataPopover } from "../../DatabaseMetadataPopover/DatabaseMetadataPopover.tsx";
import SvgEdit from "../../Icons/Edit";
import InfoTooltip from "../../InfoTooltip/InfoTooltip.tsx";
import DisableButton from "../../Table/DisableButton.tsx";
import { TableData, TableRow } from "../../Table/types.ts";
import TableFilesDisplay from "../../TableFilesDisplay/TableFilesDisplay.tsx";
import { useStyles } from "../TableRestructure.styles.ts";

interface Props {
  data: Restructure[] | null | undefined;
  canEdit?: boolean;
  onEdit?: (id: number) => void;
}

export function useTableRestructureData({
  data,
  canEdit,
  onEdit,
}: Props): TableData {
  const { classes } = useStyles();

  const { user } = useCurrentUserContext();

  const handleEdit = useCallback(
    (id: number) => {
      onEdit && onEdit(id);
    },
    [onEdit]
  );

  const columns: DataTableColumn<TableRow>[] = [
    {
      accessor: "id",
      hidden: true,
    },
    {
      accessor: "createdAt",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "Restructure",
            fieldName: "createdAt",
          }}
        >
          Created Date
        </DatabaseMetadataPopover>
      ),
      sortable: true,
      width: "150px",
    },
    {
      accessor: "customerName",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "Restructure",
            fieldName: "customer",
          }}
        >
          Customer Name
        </DatabaseMetadataPopover>
      ),
      sortable: true,
    },
    {
      accessor: "requestNumber",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "Restructure",
            fieldName: "requestNumber",
          }}
        >
          Restructure Number
        </DatabaseMetadataPopover>
      ),
      sortable: true,
      hidden: !isBababosTenant(user),
    },
    {
      accessor: "approvalStatus",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "Restructure",
            fieldName: "approvalStatus",
          }}
        >
          Approval Status
        </DatabaseMetadataPopover>
      ),
      sortable: true,
    },
    {
      accessor: "approvalDate",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "Restructure",
            fieldName: "approvalDate",
          }}
        >
          Approval Date
        </DatabaseMetadataPopover>
      ),
      sortable: true,
      width: "150px",
    },
    {
      accessor: "collectorAssigned",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "Restructure",
            fieldName: "collectorAssigned",
          }}
        >
          Collector Assigned
        </DatabaseMetadataPopover>
      ),
      sortable: true,
      width: "200px",
    },
    {
      accessor: "restructureDraftDocument",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "Restructure",
            fieldName: "restructureDraftDocument",
          }}
        >
          <Flex align="center">
            <Text>Restructure Draft Document</Text>
            <InfoTooltip tooltipText="Restructure draft document will be generated on Restructure approval" />
          </Flex>
        </DatabaseMetadataPopover>
      ),
      width: 250,
    },
    {
      accessor: "signedDocument",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "Restructure",
            fieldName: "signedDocument",
          }}
        >
          Signed Document
        </DatabaseMetadataPopover>
      ),
    },
    {
      accessor: "actions",
      title: "Actions",
      hidden: !canEdit,
    },
  ];

  const rows = useMemo(() => {
    return data?.map((item) => {
      const row: TableRow = {
        id: item.id,
        createdAt: toLocalDate(item.createdAt),
        customerName: item.customer.name,
        requestNumber: item.requestNumber,
        approvalStatus: toHumanReadable(item.approvalStatus),
        approvalDate: toLocalDate(item.approvalDate),
        collectorAssigned: item.collectorAssigned && (
          <Text className={classes.collectorAssignedWrapper}>
            {getUserDisplayName(item.collectorAssigned)}
          </Text>
        ),
        restructureDraftDocument: item.restructureDraftDocument && (
          <TableFilesDisplay files={[item.restructureDraftDocument]} />
        ),
        signedDocument: item.signedDocument && (
          <TableFilesDisplay files={[item.signedDocument]} />
        ),
        actions:
          item.approvalStatus !== RestructureApprovalStatus.Pending ? (
            <DisableButton
              text={`Editing is not allowed, restructure status is ${toHumanReadable(
                item.approvalStatus
              )}`}
            />
          ) : (
            <ActionIcon onClick={() => handleEdit(item.id)}>
              <SvgEdit />
            </ActionIcon>
          ),
      };
      return row;
    });
  }, [data, classes, handleEdit]);

  return [columns, rows];
}
